'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import {
  acquireLenis,
  currentLenis,
  releaseLenis,
} from '@/lib/motion/lenis-engine';
import { isHistoryTraversal } from '@/lib/motion/page-transition';

/** Instance type of the dynamically imported Lenis default export. */
type LenisInstance = InstanceType<typeof import('lenis').default>;

/**
 * Props for {@link LenisScrollProvider}.
 */
export interface LenisScrollProviderProps {
  /**
   * Identity of the piece currently rendered on the PDP. When it changes
   * without a full navigation — an in-page swap to a suggestion piece — the
   * provider scrolls the viewport back to the top (Req. 14.4, 14.5).
   */
  readonly activePieceId?: string;
}

/**
 * LenisScrollProvider — Client Component. Renders no UI.
 *
 * On mount it takes a reference to the product's one smooth-scroll engine
 * (`lib/motion/lenis-engine.ts`), which creates it when there is none and shares
 * it when another surface already holds one (Req. 14.1) — with a single frame
 * loop, owned there (Req. 14.2). On unmount it gives the reference back, and the
 * last holder out is what destroys the engine.
 *
 * No container ever receives `data-lenis-prevent`: this island adds no such
 * attribute anywhere on the page (Req. 14.3).
 *
 * When {@link LenisScrollProviderProps.activePieceId} changes after mount — an
 * in-page swap to a suggestion piece with no new URL — the viewport jumps
 * instantly back to the top. It uses the shared Lenis instance when available
 * (Req. 14.4), and falls back to the browser's native jump to position 0 when
 * Lenis is not yet ready (Req. 14.5).
 *
 * The PDP must ALWAYS open at the top. Because the shared Lenis engine survives
 * navigations, a route change from a scrolled vitrine would otherwise leave the
 * new PDP at the previous scroll position. To guarantee a top-of-page entry the
 * provider resets the scroll to the top ON MOUNT — right after the dynamic Lenis
 * import resolves (`lenis.scrollTo(0, { immediate: true })`), with a native
 * `window.scrollTo(0, 0)` fallback when Lenis cannot load. Since the PDP provider
 * REMOUNTS on every route change, this single mount reset covers the first load,
 * vitrine→PDP and PDP→PDP navigations alike.
 *
 * Subsequent pathname changes WHILE THE PROVIDER STAYS MOUNTED are still handled
 * by an explicit `usePathname()` effect (it jumps to the top instantly), and
 * in-page suggestion swaps with NO URL change are handled by the `activePieceId`
 * effect. The mount reset and the pathname effect do not double-fire awkwardly:
 * the pathname effect skips the FIRST pathname it observes (mount), leaving the
 * mount reset as the single source of the initial top-scroll.
 *
 * The instance is shared, so unmounting does not destroy it: another route may
 * still be driving the same engine.
 */
export function LenisScrollProvider({
  activePieceId,
}: LenisScrollProviderProps): null {
  const pathname = usePathname();
  const instanceRef = useRef<LenisInstance | null>(null);
  // The first pathname this provider observed. `null` until the first effect
  // run records it; a later pathname !== this value is a real navigation.
  const firstPathnameRef = useRef<string | null>(null);
  // Skip the mount render so the first piece does not trigger a scroll-to-top.
  const previousPieceIdRef = useRef<string | undefined>(activePieceId);

  useEffect(() => {
    let mounted = true;

    instanceRef.current = currentLenis();

    void acquireLenis().then((lenis) => {
      if (!mounted) return;
      instanceRef.current = lenis;

      // The PDP must ALWAYS open at the top. The engine outlives this mount
      // whenever another surface is holding it, so a scrolled vitrine→PDP (or
      // PDP→PDP) navigation would otherwise inherit the previous position.
      // Reset instantly — except going back or forward through history, which
      // returns to where the page was left (the person's rule, 2026-10-04).
      if (isHistoryTraversal()) return;

      // Smooth scroll is progressive enhancement: with no engine, jump natively
      // so the PDP still opens at the top (Req. 14.5).
      if (lenis === null) window.scrollTo(0, 0);
      else lenis.scrollTo(0, { immediate: true });
    });

    return () => {
      mounted = false;
      /*
       * Gives the reference back instead of leaking it.
       *
       * This used to leave the instance and its frame loop running on purpose —
       * "another route may still be driving the same engine" — which was true and
       * also meant the engine never died. One visit to a PDP left a loop running
       * for the whole session, and the next home or `/listas` built a second
       * engine on top of it: two of them writing a scroll position to the same
       * document every frame. The reference count keeps the first half of that
       * reasoning (it is not destroyed while anyone still holds it) without the
       * second (it is destroyed when nobody does).
       */
      releaseLenis();
    };
  }, []);

  // Explicit navigation signal: on the FIRST pathname do not force a scroll —
  // the mount effect above already resets to the top. On any subsequent pathname
  // change WHILE STILL MOUNTED, jump to the top instantly (Req. 14.4/14.5).
  useEffect(() => {
    if (firstPathnameRef.current === null) {
      // First pathname (mount): record it and do not scroll here — the mount
      // effect owns the initial top-scroll, so this avoids double-firing.
      firstPathnameRef.current = pathname;
      return;
    }
    if (pathname === firstPathnameRef.current) return;
    // Advance the baseline so repeated navigations each fire once.
    firstPathnameRef.current = pathname;
    if (isHistoryTraversal()) return;

    const lenis = instanceRef.current ?? currentLenis();
    if (lenis) {
      // Subsequent navigation: jump instantly to the top through the shared
      // Lenis so the new page does not inherit the previous scroll (Req. 14.4).
      lenis.scrollTo(0, { immediate: true });
      return;
    }
    // Lenis not available yet — native jump to the top (Req. 14.5).
    window.scrollTo(0, 0);
  }, [pathname]);

  // In-page swap with NO URL change: `activePieceId` changes while the pathname
  // stays the same (a suggestion swap). Jump instantly to the top.
  useEffect(() => {
    if (activePieceId === previousPieceIdRef.current) return;
    previousPieceIdRef.current = activePieceId;

    const lenis = instanceRef.current ?? currentLenis();
    if (lenis) {
      // In-page swap jumps instantly to the top through the shared Lenis
      // (Req. 14.4).
      lenis.scrollTo(0, { immediate: true });
      return;
    }

    // Lenis not available yet — native jump to the top (Req. 14.5).
    window.scrollTo({ top: 0 });
  }, [activePieceId]);

  return null;
}
